import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const LosAngelesCountyPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?county=Los+Angeles&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching LA properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Los Angeles County Real Estate | Buy or Sell a Home in LA | Truc Doan</title>
        <meta
          name="description"
          content="Looking to buy or sell a home in Los Angeles County, CA? Truc Doan is your trusted LA County real estate agent. Explore homes for sale in Long Beach, Torrance, Pasadena, Glendale, Burbank, Downey, Compton, El Monte, and more. Free home value estimates."
        />
        <meta
          name="keywords"
          content="Los Angeles County real estate, homes for sale Los Angeles County CA, buy a home Los Angeles, sell a home Los Angeles, LA County realtor, Long Beach homes for sale, Torrance real estate, Pasadena homes, Glendale real estate, Burbank homes for sale, Downey real estate, Compton homes, El Monte real estate, Inglewood homes for sale, Hawthorne real estate, Carson homes, Lakewood real estate, Whittier homes, Norwalk real estate, West Covina homes, Covina real estate, Baldwin Park homes, Pomona real estate, Truc Doan Los Angeles, VUI Group LA County, Los Angeles home value, LA real estate agent, homes for sale LA County"
        />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/los-angeles-county`} />
        <meta property="og:title" content="Los Angeles County Real Estate | Buy or Sell a Home in LA | Truc Doan" />
        <meta property="og:description" content="Your trusted Los Angeles County real estate expert. Browse homes for sale in Long Beach, Torrance, Pasadena, Glendale, Burbank & more. Free home value estimates." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/los-angeles-county`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Los Angeles County, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/los-angeles-county`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a top-rated real estate agent serving Los Angeles County, CA. Specializing in helping buyers find their dream home and sellers maximize their home value across all LA County cities.",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "Los Angeles County, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '10px 20px',
            background: 'rgb(14, 165, 233)',
            color: 'rgb(243, 244, 246)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 10
          }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>{t('la.h1')}</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            {t('la.subtitle')}
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>{t('la.whyTitle')}</h2>
          <p>{t('la.whyP1')}</p>
          <p>{t('la.whyP2')}</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>{t('county.buyingHome', { county: t('county.losAngelesCounty') })}</h2>
          <p>{t('la.buyingP')}</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('la.longBeach')}</h3>
              <p>{t('la.longBeachDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.torrance')}</h3>
              <p>{t('la.torranceDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.pasadena')}</h3>
              <p>{t('la.pasadenaDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.glendale')}</h3>
              <p>{t('la.glendaleDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.downey')}</h3>
              <p>{t('la.downeyDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.westCovina')}</h3>
              <p>{t('la.westCovinaDesc')}</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>{t('county.marketSnapshot', { county: t('county.losAngelesCounty') })}</h2>
          <p>{t('la.marketP')}</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>{t('county.marketStat')}</th>
                <th>{t('county.value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('la.medianHomePrice')}</td>
                <td>{t('la.medianHomePriceValue')}</td>
              </tr>
              <tr>
                <td>{t('county.avgDaysOnMarket')}</td>
                <td>{t('la.avgDaysValue')}</td>
              </tr>
              <tr>
                <td>{t('county.mostActivePriceRange')}</td>
                <td>{t('la.activePriceValue')}</td>
              </tr>
              <tr>
                <td>{t('county.topBuyerCities')}</td>
                <td>{t('la.topBuyerCitiesValue')}</td>
              </tr>
              <tr>
                <td>{t('county.topSellerMarkets')}</td>
                <td>{t('la.topSellerMarketsValue')}</td>
              </tr>
              <tr>
                <td>{t('la.bestValueMarkets')}</td>
                <td>{t('la.bestValueMarketsValue')}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="idxtown-vibe">
          <h2>{t('county.sellingHome', { county: t('county.losAngelesCounty') })}</h2>
          <p>{t('la.sellingP1')}</p>
          <p dangerouslySetInnerHTML={{__html: t('la.sellingP2')}} />
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('county.accuratePricing')}</h3>
              <p>{t('la.pricingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.professionalMarketing')}</h3>
              <p>{t('la.marketingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('la.bilingualTitle')}</h3>
              <p>{t('la.negotiationDesc')}</p>
            </div>
          </div>
        </section>

        <section className="properties-section">
          <h2>{t('county.currentHomesForSale', { county: t('county.losAngelesCounty') })}</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              {t('la.noListingsP')}
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>{t('county.readyToBuyOrSell', { county: t('county.losAngelesCounty') })}</h2>
          <p>{t('la.ctaP')}</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Los Angeles County Real Estate Inquiry`} className="cta-button primary">
              {t('county.emailAgent')}
            </a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">
              {t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              {t('county.searchCountyListings', { county: 'LA' })}
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default LosAngelesCountyPage;
